import moment from 'moment';
import { emptyGuid } from '@/shared/utils/guid';
import { buildServerFilters, computePeriodRange, createDefaultFilterState, } from './registrationOverviewFilterState';
export const STORAGE_KEY = 'RegistrationOverviewParams';
const KNOWN_STATE_PARAMS = [
    'relation',
    'period',
    'finalInvoiceStreamId',
    'userId',
    'dateFrom',
    'dateUntil',
    'invoiceDateFrom',
    'invoiceDateUntil',
    'resourceId',
    'trainingId',
    'invoiceId',
    'intraCompanyInvoiceId',
    'filters',
    'referenceYear',
];
const toDate = (value) => value ? moment.utc(value).toDate() : null;
export const loadSessionParams = () => {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    }
    catch (_a) {
        return null;
    }
};
/** Persists the current filters + table params (React equivalent of `retrieve`'s save). */
export const saveSessionParams = (filterState, tableParams) => {
    const filters = buildServerFilters(filterState);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign(Object.assign({ tableParams }, filters), { relation: filterState.relation, resource: filterState.resource, training: filterState.training, period: filterState.period, showAdvanceFilters: filterState.showAdvanceFilters })));
};
const restoreFromSession = (state, s) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    state.from = toDate(s.from);
    state.to = toDate(s.to);
    state.userId = s.userId === emptyGuid ? null : (_a = s.userId) !== null && _a !== void 0 ? _a : state.userId;
    state.relation = state.relation || s.relation || null;
    state.resource = s.resource || null;
    state.training = s.training || null;
    state.ownCompanies = s.ownCompanies || [];
    state.legalEntities = s.legalEntities || [];
    state.crmTypes = s.crmTypes || [];
    state.taskTemplates = s.taskTemplates || [];
    state.finalInvoiceStreamId = (_b = s.finalInvoiceStreamId) !== null && _b !== void 0 ? _b : null;
    state.withLabels = s.withLabels || [];
    state.withoutLabels = s.withoutLabels || [];
    state.withEmployeeLabels = s.withEmployeeLabels || [];
    state.withoutEmployeeLabels = s.withoutEmployeeLabels || [];
    state.relationGroups = s.relationGroups || [];
    state.period = (_c = s.period) !== null && _c !== void 0 ? _c : '';
    state.invoiceDateFrom = toDate(s.invoiceDateFrom);
    state.invoiceDateUntil = toDate(s.invoiceDateUntil);
    state.assignmentTemplateId = (_d = s.assignmentTemplateId) !== null && _d !== void 0 ? _d : null;
    state.endFiscalYears = s.endFiscalYears || [];
    state.referenceYear = (_e = s.referenceYear) !== null && _e !== void 0 ? _e : null;
    state.invoiceId = (_f = s.invoiceId) !== null && _f !== void 0 ? _f : null;
    state.intraCompanyInvoiceId = (_g = s.intraCompanyInvoiceId) !== null && _g !== void 0 ? _g : null;
    state.relationHandlingLevels = s.relationHandlingLevels || [];
    state.relationTypes = s.relationTypes || [];
    state.includeInactive = (_h = s.includeInactive) !== null && _h !== void 0 ? _h : null;
};
const applyStateParams = (state, sp) => {
    if (sp.relation && sp.relation.id)
        state.relation = sp.relation;
    if (sp.period) {
        state.period = sp.period;
        Object.assign(state, computePeriodRange(sp.period));
    }
    if (sp.finalInvoiceStreamId)
        state.finalInvoiceStreamId = sp.finalInvoiceStreamId;
    if (sp.userId)
        state.userId = sp.userId === emptyGuid ? null : sp.userId;
    if (sp.dateFrom)
        state.from = toDate(sp.dateFrom);
    if (sp.dateUntil)
        state.to = toDate(sp.dateUntil);
    if (sp.invoiceDateFrom)
        state.invoiceDateFrom = toDate(sp.invoiceDateFrom);
    if (sp.invoiceDateUntil)
        state.invoiceDateUntil = toDate(sp.invoiceDateUntil);
    if (sp.resourceId)
        state.resource = { id: sp.resourceId };
    if (sp.trainingId)
        state.training = { id: sp.trainingId };
    if (sp.invoiceId)
        state.invoiceId = sp.invoiceId;
    if (sp.intraCompanyInvoiceId)
        state.intraCompanyInvoiceId = sp.intraCompanyInvoiceId;
    if (sp.referenceYear)
        state.referenceYear = sp.referenceYear;
};
/**
 * Builds the initial filter state and table params from the UI-Router state params and
 * sessionStorage. State params take precedence over the persisted session (mirroring
 * `setParamsFromSessionStorage` + `initStateParams`); when neither is present the period
 * defaults to the current month.
 */
export const buildInitialFilterState = (stateParams, canViewOthers, currentUserId) => {
    const state = createDefaultFilterState();
    state.userId = canViewOthers ? null : currentUserId;
    const sp = stateParams || {};
    let hasStateParams = KNOWN_STATE_PARAMS.some(key => !!sp[key]);
    if (sp.relation && sp.relation.id)
        hasStateParams = true;
    const session = loadSessionParams();
    let initialTableParams = null;
    if (session)
        state.showAdvanceFilters = session.showAdvanceFilters || false;
    if (hasStateParams) {
        applyStateParams(state, sp);
        if (sp.filters)
            initialTableParams = { filter: sp.filters };
        else
            initialTableParams = {};
    }
    else if (session) {
        restoreFromSession(state, session);
        initialTableParams = session.tableParams || null;
    }
    if (!state.period) {
        state.period = 'month';
        Object.assign(state, computePeriodRange('month'));
    }
    return { filterState: state, initialTableParams };
};
